import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  getDoc,
  query,
  where,
  getCountFromServer
} from 'firebase/firestore';
import { db } from '../config/firebase';

export const fighterService = {
  // Create a new fighter
  async createFighter(fighterData) {
    const docRef = await addDoc(collection(db, 'fighters'), {
      ...fighterData,
      createdAt: new Date(),
      updatedAt: new Date()
    });

    return docRef.id;
  },


  // Update fighter
  async updateFighter(fighterId, fighterData) {
    const docRef = doc(db, 'fighters', fighterId);
    await updateDoc(docRef, {
      ...fighterData,
      updatedAt: new Date()
    });
  },

  // Delete fighter
  async deleteFighter(fighterId) {
    const docRef = doc(db, 'fighters', fighterId);
    await deleteDoc(docRef);
  },

  // Get or create fighter (useful for migration and new fighter creation)
  // Note: This now requires a DataStore instance to search existing fighters
  async getOrCreateFighter(fighterData, dataStore) {
    // If fighterData has an ID, it's already an existing fighter - just return it
    if (fighterData.id) {
      const existingFighter = dataStore.getFighter(fighterData.id);
      if (existingFighter) {
        return existingFighter;
      }
    }

    // Search for exact name match (not partial match)
    const allFighters = dataStore.getAllFighters();
    const existingFighter = allFighters.find(fighter => 
      fighter.name.toLowerCase() === fighterData.name.toLowerCase()
    );

    if (existingFighter) {
      // Return the exact match
      return existingFighter;
    }

    // Create new fighter if not found
    const fighterId = await this.createFighter(fighterData);

    // Return the basic data - DataStore will pick up the new fighter via real-time subscription
    return {
      id: fighterId,
      ...fighterData,
      createdAt: new Date(),
      updatedAt: new Date()
    };
  },

  // Get fighter statistics
  // Note: This now requires a DataStore instance to get fighter and event data
  async getFighterStats(fighterId, dataStore) {
    // Get all fights for this fighter from DataStore
    const allFights = dataStore.getAllFights ?
      dataStore.getAllFights().filter(fight =>
        fight.fighter1Id === fighterId || fight.fighter2Id === fighterId
      ).map(fight => ({
        ...fight,
        position: fight.fighter1Id === fighterId ? 'fighter1' : 'fighter2'
      })) : [];

    // If DataStore doesn't have getAllFights, fall back to direct query
    if (allFights.length === 0 && !dataStore.getAllFights) {
      const fightsQuery1 = query(
        collection(db, 'fights'),
        where('fighter1Id', '==', fighterId)
      );
      const fightsQuery2 = query(
        collection(db, 'fights'),
        where('fighter2Id', '==', fighterId)
      );

      const [fights1, fights2] = await Promise.all([
        getDocs(fightsQuery1),
        getDocs(fightsQuery2)
      ]);

      allFights.push(
        ...fights1.docs.map(doc => ({ id: doc.id, ...doc.data(), position: 'fighter1' })),
        ...fights2.docs.map(doc => ({ id: doc.id, ...doc.data(), position: 'fighter2' }))
      );
    }

    // Calculate statistics
    const totalFights = allFights.length;
    const completedFights = allFights.filter(fight => fight.status === 'completed');
    const wins = completedFights.filter(fight => fight.winner === fighterId).length;
    const losses = completedFights.filter(fight => fight.winner && fight.winner !== fighterId).length;
    const draws = completedFights.filter(fight => !fight.winner).length;

    // Get recent fights (last 5) with full data
    const recentFightsData = allFights
      .sort((a, b) => {
        const dateA = a.updatedAt?.toDate ? a.updatedAt.toDate() : a.updatedAt || new Date(0);
        const dateB = b.updatedAt?.toDate ? b.updatedAt.toDate() : b.updatedAt || new Date(0);
        return dateB - dateA;
      })
      .slice(0, 5)
      .map((fight) => {
        // Get opponent fighter data from DataStore
        const opponentId = fight.position === 'fighter1' ? fight.fighter2Id : fight.fighter1Id;
        const opponent = dataStore.getFighter(opponentId);

        // Get event data from DataStore
        const event = dataStore.getEvent(fight.eventId);

        return {
          ...fight,
          opponent,
          eventName: event?.name || 'Unknown Event',
          eventDate: event?.date,
          updatedAt: fight.updatedAt?.toDate ? fight.updatedAt.toDate() : fight.updatedAt,
          createdAt: fight.createdAt?.toDate ? fight.createdAt.toDate() : fight.createdAt
        };
      });

    return {
      totalFights,
      wins,
      losses,
      draws,
      winPercentage: completedFights.length > 0 ? (wins / completedFights.length) * 100 : 0,
      recentFights: recentFightsData,
      isActive: allFights.some(fight => fight.status === 'upcoming' || fight.status === 'active')
    };
  },

  // Get fighter count
  async getFighterCount() {
    const countSnapshot = await getCountFromServer(collection(db, 'fighters'));
    return countSnapshot.data().count;
  }
};
